import { format } from "date-fns";
import _ from "lodash";

export const filterCompanyVersions = ({
  dateFrom,
  dateTo,
  company,
  companyOfficials,
}) => {
  const formatDate = (date) => {
    if (!date?.getTime()) {
      // Return company's date of registration
      return format(new Date(company.registrationDate), "yyyy/MM/dd");
    }
    return format(date, "yyyy/MM/dd");
  };

  // Return empty result if input is invalid
  if (
    !company ||
    !companyOfficials ||
    !Array.isArray(companyOfficials) ||
    !dateFrom ||
    !dateTo
  ) {
    return {
      hasChange: false,
      latestVersion: [],
      previousVersion: [],
      officialsInRange: [],
    };
  }

  const fromDate = formatDate(new Date(dateFrom));
  const toDate = formatDate(new Date(dateTo));
  let companyHasChanges = false;

  // Filter officials by appointmentDate or cessationDate within the date range for officialsInRange
  const officialsInRange = companyOfficials
    .filter((official) => {
      const appointmentDate = formatDate(new Date(official.appointmentDate));
      const cessationDate = official.cessationDate
        ? formatDate(new Date(official.cessationDate))
        : null;
      return (
        (appointmentDate >= fromDate && appointmentDate <= toDate) ||
        (cessationDate && cessationDate >= fromDate && cessationDate <= toDate)
      );
    })
    .map((official) => ({
      _id: official._id,
      designation: official.designation,
      appointmentDate: official.appointmentDate,
      cessationDate: official.cessationDate,
      names: official.names
        ? {
            firstName: official.names.firstName,
            lastName: official.names.lastName,
            companyName: official.names.companyName,
          }
        : {},
    }));

  // Filter active officials: appointmentDate <= toDate and (no cessationDate or cessationDate >= fromDate)
  const activeOfficials = companyOfficials.filter((official) => {
    const appointmentDate = official?.appointmentDate
      ? formatDate(new Date(official.appointmentDate))
      : null;
    const cessationDate = official.cessationDate
      ? formatDate(new Date(official.cessationDate))
      : null;
    return (
      appointmentDate <= toDate &&
      (appointmentDate <= fromDate || appointmentDate >= fromDate) &&
      (!cessationDate || cessationDate >= toDate)
    );
  });

  // Collect all versions from active officials
  const allOfficialVersions = activeOfficials.flatMap((official) => [
    ...(official.names?.versions || []).map((v) => ({
      ...v,
      section: "names",
      officialId: official._id,
    })),
    ...(official.electronicAddress?.versions || []).map((v) => ({
      ...v,
      section: "electronicAddress",
      officialId: official._id,
    })),
    ...(official.identificationDetails?.versions || []).map((v) => ({
      ...v,
      section: "identificationDetails",
      officialId: official._id,
    })),
    ...(official.postalAddress?.versions || []).map((v) => ({
      ...v,
      section: "postalAddress",
      officialId: official._id,
    })),
    ...(official.residentialAddressDetails?.versions || []).map((v) => ({
      ...v,
      section: "residentialAddressDetails",
      officialId: official._id,
    })),
  ]);

  // Filter versions within the date range for latestVersion
  const officialVersionsInRange = allOfficialVersions.filter((version) => {
    const versionDate = version?.effectiveFrom
      ? formatDate(new Date(version.effectiveFrom))
      : null;
    return versionDate >= fromDate && versionDate <= toDate;
  });

  // Filter versions before the date range for previousVersion
  const officialVersionsBeforeRange = allOfficialVersions.filter((version) => {
    const versionDate = version?.effectiveFrom
      ? formatDate(new Date(version.effectiveFrom))
      : null;
    return versionDate < fromDate;
  });

  // Group versions by officialId and section
  const officialGroupedVersionsInRange = _.groupBy(
    officialVersionsInRange,
    (v) => `${v.officialId}_${v.section}`
  );

  const officialGroupedVersionsBeforeRange = _.groupBy(
    officialVersionsBeforeRange,
    (v) => `${v.officialId}_${v.section}`
  );

  // Company summary details versions within date range
  const companySummaryVersionsInRange = company.versions.filter((version) => {
    const versionDate = version?.effectiveFrom
      ? formatDate(new Date(version.effectiveFrom))
      : null;
    return versionDate >= fromDate && versionDate <= toDate;
  });

  //Show info marker on year tabs if there are changes in company summary details
  if (companySummaryVersionsInRange.length > 0) {
    if (
      companySummaryVersionsInRange.length === 1 &&
      companySummaryVersionsInRange.some((v) => v.version === 1)
    ) {
      companyHasChanges = false;
    } else {
      companyHasChanges = true;
    }
  }

  //Filter version before date range for the company summary details
  const companySummaryVersionsBeforeRange = company.versions.filter(
    (version) => {
      const versionDate = version?.effectiveFrom
        ? formatDate(new Date(version.effectiveFrom))
        : null;
      return versionDate < fromDate;
    }
  );

  // Initialize result
  const latestVersion = {
    officials: [],
    companySummary: {},
  };
  const previousVersion = {
    officials: [],
    companySummary: {},
  };

  const fieldsToPick = [
    "articlesOfAssociation",
    "registrationName",
    "status",
    "type",
    "registrationNumber",
  ];

  if (companySummaryVersionsInRange.length > 1) {
    // Multiple versions in range: use latest
    latestVersion.companySummary = _.pick(
      companySummaryVersionsInRange[companySummaryVersionsInRange.length - 1],
      fieldsToPick
    );
    previousVersion.companySummary = _.pick(
      companySummaryVersionsInRange[companySummaryVersionsInRange.length - 2],
      fieldsToPick
    );
  } else if (companySummaryVersionsInRange.length === 1) {
    if (companySummaryVersionsInRange.some((v) => v.version === 1)) {
      // Only one version in range
      latestVersion.companySummary = _.pick(
        companySummaryVersionsInRange[0],
        fieldsToPick
      );
      previousVersion.companySummary = _.pick(
        companySummaryVersionsInRange[0],
        fieldsToPick
      );
    } else {
      // Only one version in range
      latestVersion.companySummary = _.pick(
        companySummaryVersionsInRange[0],
        fieldsToPick
      );
      previousVersion.companySummary = _.pick(
        companySummaryVersionsBeforeRange[0],
        fieldsToPick
      );
    }
  } else {
    // No versions in range: use latest before-range version
    latestVersion.companySummary = _.pick(
      companySummaryVersionsBeforeRange[0],
      fieldsToPick
    );
    previousVersion.companySummary = _.pick(
      companySummaryVersionsBeforeRange[0],
      fieldsToPick
    );
  }

  // Process each active official
  activeOfficials.forEach((official) => {
    const officialId = official._id;
    const isInRange = officialsInRange.some((o) => o._id === officialId);

    // Initialize official data with original values
    const latestOfficial = { ...official };
    const previousOfficial = { ...official };

    // Add markers based on appointmentDate and cessationDate
    const appointmentDate = official?.appointmentDate
      ? formatDate(new Date(official.appointmentDate))
      : null;
    const cessationDate = official.cessationDate
      ? formatDate(new Date(official.cessationDate))
      : null;
    latestOfficial.appointmentMarker =
      appointmentDate >= fromDate && appointmentDate <= toDate;
    latestOfficial.cessationMarker =
      cessationDate && cessationDate >= fromDate && cessationDate <= toDate;

    latestOfficial.changeMarker = officialVersionsInRange.some(
      (v) =>
        v.officialId === officialId &&
        [
          "names",
          "electronicAddress",
          "identificationDetails",
          "postalAddress",
          "residentialAddressDetails",
        ].includes(v.section) &&
        formatDate(new Date(v?.effectiveFrom)) >= fromDate &&
        (formatDate(new Date(v?.effectiveTo)) <= toDate || !v?.effectiveTo)
    );

    previousOfficial.appointmentMarker = latestOfficial.appointmentMarker;
    previousOfficial.cessationMarker = latestOfficial.cessationMarker;

    // Apply latest and previous versions
    [
      "names",
      "electronicAddress",
      "identificationDetails",
      "postalAddress",
      "residentialAddressDetails",
    ].forEach((section) => {
      // In-range versions
      const sectionVersionsInRange =
        officialGroupedVersionsInRange[`${officialId}_${section}`] || [];
      const sortedSectionVersionsInRange = _.orderBy(
        sectionVersionsInRange,
        ["createdAt"],
        ["desc"]
      );

      // Before-range versions
      const sectionVersionsBeforeRange =
        officialGroupedVersionsBeforeRange[`${officialId}_${section}`] || [];
      const sortedSectionVersionsBeforeRange = _.orderBy(
        sectionVersionsBeforeRange,
        ["createdAt"],
        ["desc"]
      );

      // Latest version (in range)
      if (sortedSectionVersionsInRange[0]) {
        latestOfficial[section] = sortedSectionVersionsInRange[0];
      }

      // Previous version logic
      if (sortedSectionVersionsInRange.length > 1) {
        // Multiple versions in range: use second-latest
        previousOfficial[section] = sortedSectionVersionsInRange[1];
      } else if (sortedSectionVersionsInRange.length === 1) {
        // Only one version in range
        if (sortedSectionVersionsBeforeRange[0]) {
          // Use most recent version before range
          previousOfficial[section] = sortedSectionVersionsBeforeRange[0];
        } else {
          // No versions before range: use the in-range version
          previousOfficial[section] = sortedSectionVersionsInRange[0];
        }
      }
      // If no versions in range, previousOfficial retains original data
    });

    // Include official in latestVersion if they have versions or are in officialsInRange
    if (
      latestOfficial.names !== official.names ||
      latestOfficial.electronicAddress !== official.electronicAddress ||
      latestOfficial.identificationDetails !== official.identificationDetails ||
      latestOfficial.postalAddress !== official.postalAddress ||
      latestOfficial.residentialAddressDetails !==
        official.residentialAddressDetails ||
      isInRange
    ) {
      latestVersion.officials.push(latestOfficial);
    } else {
      latestVersion.officials.push({
        ...official,
        appointmentMarker: false,
        cessationMarker: false,
      });
    }

    // Include in previousVersion if there’s a previous version or in officialsInRange
    if (
      previousOfficial.names !== official.names ||
      previousOfficial.electronicAddress !== official.electronicAddress ||
      previousOfficial.identificationDetails !==
        official.identificationDetails ||
      previousOfficial.postalAddress !== official.postalAddress ||
      previousOfficial.residentialAddressDetails !==
        official.residentialAddressDetails ||
      isInRange
    ) {
      previousVersion.officials.push(previousOfficial);
    } else {
      previousVersion.officials.push({
        ...official,
        appointmentMarker: false,
        cessationMarker: false,
      });
    }
  });

  // Copy company details

  return {
    hasChange:
      officialVersionsInRange.length > 0 ||
      officialsInRange.length > 0 ||
      companyHasChanges,
    latestVersion,
    previousVersion,
    officialsInRange,
  };
};
